import { Key, Modal } from "@xola/ui-kit";
import { orderBy } from "lodash";
import React, { useMemo, useState } from "react";
import { Delegator } from "types";
import { DelegatorTile } from "@/components/Common/DelegatorTile";
import { SearchInput } from "@/components/Common/SearchInput";
import { useSearch } from "@/hooks/useSearch";
import { useAccountStore } from "@/stores/accountStore";

interface AccountListModalProps {
    title: string;
    delegators: Delegator[];
    isOpen: boolean;
    onClose?: () => void;
    onSelect: (delegator: Delegator) => void;
    shouldCloseOnOutsideClick?: boolean;
}

export const AccountListModal = ({
    title,
    delegators,
    isOpen,
    onClose,
    onSelect,
    shouldCloseOnOutsideClick = true,
}: AccountListModalProps) => {
    return (
        <Modal isOpen={isOpen} shouldCloseOnOutsideClick={shouldCloseOnOutsideClick} className="!p-0" onClose={onClose}>
            <AccountListModalBody title={title} delegators={delegators} onSelect={onSelect} />
        </Modal>
    );
};

interface AccountListModalBodyProps {
    title: string;
    delegators: Delegator[];
    onSelect: (delegator: Delegator) => void;
}

const AccountListModalBody = ({ title, delegators, onSelect }: AccountListModalBodyProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const currentSellerId = useAccountStore((state) => state.seller);
    const isMac = navigator.userAgent.includes("Macintosh");

    const alphabeticSortedDelegators = useMemo(() => orderBy(delegators, ["name"], ["asc"]), [delegators]);
    const sortedDelegators = useMemo(() => {
        // Move the current seller to the top of the list, and sort the rest alphabetically below
        const result: Delegator[] = [];
        for (const delegator of alphabeticSortedDelegators) {
            if (delegator.id === currentSellerId) {
                result.unshift(delegator);
                continue;
            }
            result.push(delegator);
        }
        return result;
    }, [alphabeticSortedDelegators, currentSellerId]);

    const filteredDelegators = useSearch(alphabeticSortedDelegators, (d) => [d.name], searchTerm || null);
    const displayDelegators = searchTerm ? filteredDelegators : sortedDelegators;

    const shortcutAdornment = (
        <div className="flex items-center space-x-0.5">
            <Key char={isMac ? "cmd" : "ctrl"} className="no-translate" />
            <Key char="O" className="no-translate" />
        </div>
    );

    return (
        <>
            <Modal.Header className="py-6">{title}</Modal.Header>
            <div className="px-3 pb-3">
                <SearchInput
                    shouldFocus
                    value={searchTerm}
                    placeholder="Search accounts"
                    rightAdornment={shortcutAdornment}
                    onChange={setSearchTerm}
                />
            </div>
            <Modal.Body className="!mt-0 max-h-96 overflow-auto">
                <div className="divide-y divide-gray-lighter">
                    {displayDelegators.map((delegator) => (
                        <DelegatorTile
                            key={delegator.id}
                            delegator={delegator}
                            isActive={delegator.id === currentSellerId}
                            onClick={() => onSelect(delegator)}
                        />
                    ))}
                    {displayDelegators.length === 0 ? (
                        <p className="p-4 text-center text-gray-dark">No accounts found</p>
                    ) : null}
                </div>
            </Modal.Body>
        </>
    );
};
